/**
 * Phase 7 · CRM: customer profiles with loyalty points, vouchers, and
 * feedback with management responses.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, Star } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, num, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select, Textarea } from '../components/ui/Input';

interface Customer { id: string; full_name: string; phone: string | null; email: string | null; points_balance: number; total_spend: number; visits: number }
interface Feedback { id: string; rating: number; comment: string | null; response: string | null; channel: string; created_at: string; customer_name?: string | null }
interface Voucher { id: string; code: string; kind: string; value: number; status: string }

const TABS = ['Customers', 'Vouchers', 'Feedback'] as const;

export function CrmPage() {
  const canLoyalty = useAuth((s) => s.user?.permissions.includes('crm:loyalty') ?? false);
  const canFeedback = useAuth((s) => s.user?.permissions.includes('crm:feedback') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Customers');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [custModal, setCustModal] = useState<Partial<Customer> | null>(null);
  const [pointsFor, setPointsFor] = useState<Customer | null>(null);
  const [voucherModal, setVoucherModal] = useState(false);
  const [respondTo, setRespondTo] = useState<Feedback | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        http.get<{ customers: Customer[] }>('/crm/customers'),
        http.get<{ feedback: Feedback[] }>('/crm/feedback'),
      ]);
      setCustomers(c.customers); setFeedback(f.feedback);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">Customers</h1>
        <div className="flex gap-2">
          {canLoyalty && <Button variant="secondary" onClick={() => setVoucherModal(true)}><Plus size={16} /> Voucher</Button>}
          <Button onClick={() => setCustModal({})}><Plus size={16} /> Customer</Button>
        </div>
      </header>

      <div className="flex gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Customers' && (
        <DataTable columns={[
          { key: 'full_name', header: 'Customer', sortable: true },
          { key: 'phone', header: 'Phone', render: (r: Customer) => r.phone ?? '—' },
          { key: 'visits', header: 'Visits', render: (r: Customer) => num(r.visits) },
          { key: 'total_spend', header: 'Lifetime spend', render: (r: Customer) => money(r.total_spend) },
          { key: 'points_balance', header: 'Points', render: (r: Customer) => <Badge tone="brand">{num(r.points_balance)} pts</Badge> },
          ...(canLoyalty ? [{ key: 'act', header: '', render: (r: Customer) => <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setPointsFor(r)}>± points</Button><Button size="sm" variant="secondary" onClick={() => setCustModal(r)}>Edit</Button></div> }] as Column<Customer>[] : []),
        ]} rows={customers} loading={loading} />
      )}

      {tab === 'Vouchers' && <VouchersTab canManage={canLoyalty} />}

      {tab === 'Feedback' && (
        <DataTable columns={[
          { key: 'created_at', header: 'When', render: (r: Feedback) => dateTime(r.created_at) },
          { key: 'rating', header: 'Rating', render: (r: Feedback) => <span aria-label={`${r.rating} stars`} className="text-amber-500">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span> },
          { key: 'comment', header: 'Comment', render: (r: Feedback) => <span className="line-clamp-2 max-w-md">{r.comment ?? '—'}</span> },
          { key: 'channel', header: 'Via' },
          { key: 'response', header: 'Responded', render: (r: Feedback) => r.response ? <Badge tone="success">Yes</Badge> : <Badge>Not yet</Badge> },
          ...(canFeedback ? [{ key: 'act', header: '', render: (r: Feedback) => !r.response ? <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => setRespondTo(r)}><Star size={13} /> Respond</Button></div> : null }] as Column<Feedback>[] : []),
        ]} rows={feedback} loading={loading} />
      )}

      {custModal && <CustomerModal initial={custModal} onClose={() => setCustModal(null)} onSaved={() => { setCustModal(null); void load(); }} />}
      {pointsFor && <PointsModal customer={pointsFor} onClose={() => setPointsFor(null)} onSaved={() => { setPointsFor(null); void load(); }} />}
      {voucherModal && <VoucherModal customers={customers} onClose={() => setVoucherModal(false)} />}
      {respondTo && <RespondModal feedback={respondTo} onClose={() => setRespondTo(null)} onSaved={() => { setRespondTo(null); void load(); }} />}
    </div>
  );
}

function VouchersTab({ canManage }: { canManage: boolean }) {
  void canManage;
  const [code, setCode] = useState('');
  const [found, setFound] = useState<Voucher | null>(null);
  const [msg, setMsg] = useState('');
  const lookup = async () => {
    try {
      const r = await http.get<{ voucher: Voucher }>(`/crm/vouchers/${code.trim()}`);
      setFound(r.voucher); setMsg('');
    } catch (e) { setFound(null); setMsg(e instanceof ApiError ? e.message : String(e)); }
  };
  return (
    <div className="max-w-md space-y-3 rounded-xl border border-line bg-surface-0 p-4">
      <p className="text-sm text-ink-muted">Look up a voucher by code.</p>
      <div className="flex gap-2">
        <Input aria-label="Voucher code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. GIFT-1234" />
        <Button variant="secondary" onClick={() => void lookup()}>Look up</Button>
      </div>
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      {found && (
        <div className="space-y-1 text-sm text-ink">
          <p><strong>{found.code}</strong> <StatusBadge status={found.status} /></p>
          <p>{found.kind} · {found.kind === 'percent' ? `${found.value}%` : money(found.value)}</p>
        </div>
      )}
    </div>
  );
}

function CustomerModal({ initial, onClose, onSaved }: { initial: Partial<Customer>; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const editing = Boolean(initial.id);
  const [fullName, setFullName] = useState(initial.full_name ?? '');
  const [phone, setPhone] = useState(initial.phone ?? '');
  const [email, setEmail] = useState(initial.email ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { fullName, phone: phone || null, email: email || null };
      if (editing) await http.patch(`/crm/customers/${initial.id}`, body);
      else await http.post('/crm/customers', body);
      toast({ kind: 'success', title: editing ? 'Customer updated' : 'Customer created' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Save failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={editing ? 'Edit customer' : 'New customer'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3">
        <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
    </Modal>
  );
}

function PointsModal({ customer, onClose, onSaved }: { customer: Customer; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [points, setPoints] = useState('100');
  const [reason, setReason] = useState<'adjust' | 'bonus' | 'expire'>('bonus');
  const [note, setNote] = useState('Loyalty award');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!note.trim()) { toast({ kind: 'error', title: 'A note is required' }); return; }
    setBusy(true);
    try {
      await http.post(`/crm/customers/${customer.id}/points`, { points: parseInt(points, 10) || 0, reason, note: note.trim() });
      toast({ kind: 'success', title: 'Points updated', message: customer.full_name });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Points · ${customer.full_name}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void run()}>Apply</Button></>}>
      <div className="grid gap-3">
        <Input label="Points (negative to deduct)" type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
        <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value as 'adjust' | 'bonus' | 'expire')}>
          <option value="bonus">Bonus award</option>
          <option value="adjust">Manual adjustment</option>
          <option value="expire">Expiry / deduction</option>
        </Select>
        <Input label="Note (shown on the loyalty ledger)" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}

function VoucherModal({ customers, onClose }: { customers: Customer[]; onClose: () => void }) {
  const toast = useUi((s) => s.toast);
  const [kind, setKind] = useState<'fixed' | 'percent' | 'free_item'>('fixed');
  const [value, setValue] = useState('10');
  const [customerId, setCustomerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await http.post<{ voucher: { code: string } }>('/crm/vouchers', {
        kind, customerId: customerId || null,
        value: kind === 'fixed' ? Math.round(parseFloat(value || '0') * 100) : parseInt(value || '0', 10),
      });
      setCode(r.voucher.code);
      toast({ kind: 'success', title: 'Voucher created', message: r.voucher.code });
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New voucher"
      footer={<><Button variant="secondary" onClick={onClose}>Close</Button>{!code && <Button loading={busy} onClick={() => void run()}>Create</Button>}</>}>
      {code ? (
        <p className="text-sm text-ink">Voucher code: <strong className="rounded bg-surface-1 px-2 py-1 font-mono">{code}</strong></p>
      ) : (
        <div className="grid gap-3">
          <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as never)}>
            <option value="fixed">Fixed amount (£)</option><option value="percent">Percent</option><option value="free_item">Free item</option>
          </Select>
          <Input label="Value" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} />
          <Select label="Customer (optional)" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Anyone</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </Select>
        </div>
      )}
    </Modal>
  );
}

function RespondModal({ feedback, onClose, onSaved }: { feedback: Feedback; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [response, setResponse] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await http.post(`/crm/feedback/${feedback.id}/respond`, { response });
      toast({ kind: 'success', title: 'Response saved' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Respond to feedback"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!response.trim()} onClick={() => void run()}>Send</Button></>}>
      <p className="mb-3 text-sm text-ink-muted">“{feedback.comment ?? '(no comment)'}”</p>
      <Textarea label="Your response" rows={4} value={response} onChange={(e) => setResponse(e.target.value)} />
    </Modal>
  );
}
